type Nullable<T> = T | null | undefined;

// GraphQL-style edge node type
interface Edge<T> {
  node: Nullable<T>;
}

// GraphQL-style connection
interface Connection<T> {
  edges?: Nullable<Array<Nullable<Edge<T>>>>;
}

// Generic object map
type ObjectMap = { [key: string]: unknown };

/**
 * Strips GraphQL connection edges and nodes from the input.
 * This function recursively processes the input to remove GraphQL-style edges and nodes,
 * returning only the node data.
 *
 * @param input - The input data, which can be an object, array, or primitive value.
 * @returns The processed data with GraphQL connections stripped.
 */
export function removeEdgesAndNodes<T>(input: unknown): Nullable<T> {
  if (Array.isArray(input)) {
    return input.map(removeEdgesAndNodes) as Nullable<T>;
  }

  if (typeof input === 'object' && input !== null) {
    if ('edges' in input && Array.isArray((input as Connection<unknown>).edges)) {
      return (input as Connection<unknown>).edges
        ?.filter((e): e is Edge<unknown> => e?.node != null)
        .map((e) => removeEdgesAndNodes(e.node)) as Nullable<T>;
    }

    const result: ObjectMap = {};
    for (const [key, value] of Object.entries(input)) {
      result[key] = removeEdgesAndNodes(value);
    }
    return result as Nullable<T>;
  }
  return input as Nullable<T>;
}
